/**
 * Shared utility for sanitizing filters before sending to API
 * Removes undefined/null/empty values and converts known boolean fields from strings
 */

/**
 * Fields that should be converted from string to boolean
 * Add more fields here as needed
 */
const BOOLEAN_FIELDS = ['active'] as const;

type BooleanField = (typeof BOOLEAN_FIELDS)[number];

/**
 * Sanitizes filters by:
 * 1. Removing undefined/null/empty string values
 * 2. Converting known boolean fields from string 'true'/'false' to boolean
 *
 * @param filters - The filters object to sanitize
 * @returns Sanitized filters object with proper types
 */
export function sanitizeFilters(
  filters: Record<string, unknown> | undefined | null
): Record<string, string | boolean> {
  if (!filters) {
    return {};
  }

  const sanitized: Record<string, string | boolean> = {};

  for (const [key, value] of Object.entries(filters)) {
    // Skip undefined, null, or empty string values
    if (value === undefined || value === null || value === '') {
      continue;
    }

    // Convert known boolean fields from string to boolean
    if (BOOLEAN_FIELDS.includes(key as BooleanField)) {
      sanitized[key] = value === 'true' || value === true;
    } else {
      sanitized[key] = value as string;
    }
  }

  return sanitized;
}
